import ThumbDown from "@mui/icons-material/ThumbDown";
import ThumbUp from "@mui/icons-material/ThumbUp";
import { Box, Stack, Tooltip, Typography } from "@mui/material";
import { GridRowParams, GridSortModel } from "@mui/x-data-grid-premium";
import { ExportDataButton } from "components/ExportDataButton";
import { type FilterGroup } from "components/Omnisearch/FilterPopover";
import { UserType } from "encamp-prisma/generated/browser";
import { prettyPrintEnumValue } from "encamp-shared/src/utils/prettyPrintEnumValue";
import { gql } from "generated-graphql";
import {
  GetScoutConversationsForStaffQuery,
  GetScoutConversationsForStaffQueryVariables,
} from "generated-graphql/graphql";
import { OmnisearchDataGridV2, type OmnisearchCommandButtonsContext } from "hooks/omnisearch-v2";
import { useBreadcrumb } from "hooks/useBreadcrumbs";
import { OmnisearchGridColDef, type FilterKey } from "hooks/useOmnisearchDatagrid";
import { useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";

export const GET_SCOUT_CONVERSATIONS_FOR_STAFF = gql(`
  query GetScoutConversationsForStaff(
    $search: String
    $page: Int
    $pageSize: Int
    $sort: [SortModel!]
  ) {
    getScoutConversationsForStaff(
      search: $search
      page: $page
      pageSize: $pageSize
      sort: $sort
    ) {
      count
      items {
        id
        title
        previewExcerpt
        status
        updatedAt
        createdAt
        tenant {
          id
          name
          scoutPackage
        }
        user {
          id
          email
          firstName
          lastName
        }
        usage {
          model
          inputTokens
          outputTokens
          thinkingTokens
          cacheReadTokens
          cacheWriteTokens
          estimatedCostUsd
        }
        feedback {
          upvotes
          downvotes
        }
        staffFeedback {
          id
          rating
          feedback
          submittedByEmail
          createdAt
          updatedAt
        }
      }
    }
  }
`);

type ScoutConversationStaffRow =
  GetScoutConversationsForStaffQuery["getScoutConversationsForStaff"]["items"][number];

const PAGE_SIZE_OPTIONS = [25, 50, 100];
const INITIAL_SORT_MODEL: GridSortModel = [{ field: "updatedAt", sort: "desc" }];

const FILTER_GROUPS: FilterGroup[] = [
  {
    name: "Organization and User",
    fields: ["tenant", "scoutPackage", "userType"],
  },
];

// Values are the `scoutPackage:` tokens the backend handler matches on. `none`
// is display-only — it labels tenants with neither Scout flag enabled and is
// not offered as a filter option, since the backend has no predicate for it.
const SCOUT_PACKAGE_LABELS: Record<string, string> = {
  pro: "Scout Pro",
  regulatory_advisor: "Scout Regulatory Advisor",
};

function scoutPackageLabel(value: string): string {
  if (value === "none") {
    return "None";
  }
  return SCOUT_PACKAGE_LABELS[value] ?? value;
}

const ADDITIONAL_FILTER_KEYS: FilterKey[] = [
  {
    key: "messages",
    header: "Messages",
    filterKeyType: "text",
  },
  {
    key: "userType",
    header: "User Type",
    filterKeyType: "enum",
    enumValues: Object.values(UserType),
    enumPresentationFunction: (value) => prettyPrintEnumValue(value),
  },
  {
    key: "scoutPackage",
    header: "Scout Package",
    filterKeyType: "enum",
    enumValues: Object.keys(SCOUT_PACKAGE_LABELS),
    enumPresentationFunction: (value) => scoutPackageLabel(value),
  },
];

const EXCLUDE_FILTER_COLUMNS = [
  "previewExcerpt",
  "updatedAt",
  "staffFeedback",
  "model",
  "inputTokens",
  "outputTokens",
  "thinkingTokens",
  "cacheReadTokens",
  "cacheWriteTokens",
  "estimatedCostUsd",
];

const getItems = (data: GetScoutConversationsForStaffQuery) =>
  data.getScoutConversationsForStaff.items;
const getCount = (data: GetScoutConversationsForStaffQuery) =>
  data.getScoutConversationsForStaff.count;

function feedbackFilterLabel(value: string): string {
  if (value === "upvoted") {
    return "Upvoted";
  }
  if (value === "downvoted") {
    return "Downvoted";
  }
  return value;
}

// Compact "$0.0023" / "$1.42" formatting — staff observer values typically
// sit in the cents range, so fixed 4 decimals reads well next to integer
// token counts in the table.
function formatUsdCost(value: number): string {
  if (!Number.isFinite(value) || value === 0) {
    return "$0.0000";
  }
  return `$${value.toFixed(4)}`;
}

// Match `formatTokens` in encamp-scout/core/src/telemetry/pricing.ts so the
// staff list and the per-message viewer agree on how to compact "12345" into
// "12.3K". Inlined rather than imported because oak doesn't depend on the
// scout core runtime.
function formatTokensCompact(tokens: number): string {
  if (tokens >= 1_000_000) {
    return `${(tokens / 1_000_000).toFixed(2)}M`;
  }
  if (tokens >= 1_000) {
    return `${(tokens / 1_000).toFixed(1)}K`;
  }
  return tokens.toString();
}

export function ScoutConversations() {
  useBreadcrumb({ label: "Scout Conversations" });
  const navigate = useNavigate();

  const columns: OmnisearchGridColDef<ScoutConversationStaffRow>[] = useMemo(
    () => [
      {
        field: "updatedAt",
        headerName: "Updated",
        flex: 0.4,
        type: "dateTime",
        valueGetter(_, row) {
          return row.updatedAt ? new Date(row.updatedAt) : null;
        },
      },
      {
        field: "tenant",
        headerName: "Tenant",
        flex: 0.6,
        sortable: true,
        // multipleTenant renders MultipleTenantPicker in the filter chip
        // popover so staff can pick tenants from a searchable dropdown
        // instead of typing exact names. The picker serializes the
        // selection as `tenantId:uuid1|uuid2`; the backend handler
        // resolves that to `tenantId: { in: [...] }`.
        filterKeyType: "multipleTenant",
        valueGetter(_, row) {
          return row.tenant?.name ?? "(no tenant)";
        },
      },
      {
        field: "scoutPackage",
        headerName: "Scout Package",
        flex: 0.5,
        sortable: false,
        // Filtering routes through the `scoutPackage:` omnisearch key declared
        // in ADDITIONAL_FILTER_KEYS, which resolves against the tenant's
        // feature flags server-side.
        filterable: false,
        valueGetter(_, row) {
          return scoutPackageLabel(row.tenant?.scoutPackage ?? "none");
        },
      },
      {
        field: "user",
        headerName: "User",
        flex: 0.6,
        sortable: true,
        // multipleStaffAnyUser routes the picker through the
        // staffSearchAnyUser query, which has no tenant scope and no
        // user-type restriction. The plain `multipleUser` picker would
        // throw "No current tenant id set in context" here because the
        // tenant `users` resolver requires a current-tenant context that
        // /staff routes don't have. Selected users serialize as pipe-
        // separated emails, resolved server-side via prisma email lookup.
        filterKeyType: "multipleStaffAnyUser",
        valueGetter(_, row) {
          return row.user?.email ?? row.user?.id ?? "(unknown)";
        },
      },
      {
        field: "title",
        headerName: "Title",
        flex: 0.6,
        valueGetter(_, row) {
          return row.title ?? "(untitled)";
        },
      },
      {
        field: "previewExcerpt",
        headerName: "Preview",
        flex: 1,
        sortable: false,
        valueGetter(_, row) {
          return row.previewExcerpt ?? "";
        },
      },
      {
        field: "status",
        headerName: "Status",
        flex: 0.3,
      },
      {
        field: "feedback",
        headerName: "Feedback",
        flex: 0.5,
        sortable: false,
        // Single-select dropdown so staff don't have to know the
        // accepted values. enumPresentationFunction renders title-cased
        // labels; the lowercased option strings are what the backend
        // handler matches on (`feedback:upvoted`/`feedback:downvoted`).
        filterKeyType: "enum",
        enumValues: ["upvoted", "downvoted"],
        enumPresentationFunction: feedbackFilterLabel,
        valueGetter(_, row) {
          return row.feedback.upvotes - row.feedback.downvotes;
        },
        renderCell: (params) => {
          const upvotes = params.row.feedback.upvotes;
          const downvotes = params.row.feedback.downvotes;
          if (upvotes === 0 && downvotes === 0) {
            return (
              <Typography variant="body2" color="text.disabled">
                —
              </Typography>
            );
          }
          return (
            <Stack direction="row" spacing={1.5} alignItems="center">
              {upvotes > 0 ? (
                <Stack direction="row" spacing={0.5} alignItems="center">
                  <ThumbUp sx={{ fontSize: 14, color: "success.main" }} />
                  <Typography variant="body2">{upvotes}</Typography>
                </Stack>
              ) : null}
              {downvotes > 0 ? (
                <Stack direction="row" spacing={0.5} alignItems="center">
                  <ThumbDown sx={{ fontSize: 14, color: "error.main" }} />
                  <Typography variant="body2">{downvotes}</Typography>
                </Stack>
              ) : null}
            </Stack>
          );
        },
      },
      {
        field: "staffFeedback",
        headerName: "Staff Feedback",
        flex: 0.8,
        sortable: false,
        filterable: false,
        valueGetter(_, row) {
          return row.staffFeedback?.feedback ?? "";
        },
        renderCell: (params) => {
          const staffFeedback = params.row.staffFeedback;
          const feedbackText = staffFeedback?.feedback?.trim() ?? "";
          if (!staffFeedback || !feedbackText) {
            return (
              <Typography variant="body2" color="text.disabled">
                —
              </Typography>
            );
          }

          return (
            <Tooltip
              arrow
              placement="top"
              title={
                <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
                  {feedbackText}
                </Typography>
              }
            >
              <Typography
                variant="body2"
                noWrap
                sx={{ overflow: "hidden", textOverflow: "ellipsis", width: "100%" }}
              >
                {feedbackText}
              </Typography>
            </Tooltip>
          );
        },
      },
      {
        field: "model",
        headerName: "Model",
        flex: 0.55,
        sortable: false,
        valueGetter(_, row) {
          return row.usage.model ?? "—";
        },
      },
      {
        field: "inputTokens",
        headerName: "Input tokens",
        flex: 0.4,
        type: "number",
        sortable: false,
        valueGetter(_, row) {
          return row.usage.inputTokens;
        },
        valueFormatter: (value) => formatTokensCompact(Number(value ?? 0)),
      },
      {
        field: "outputTokens",
        headerName: "Output tokens",
        flex: 0.4,
        type: "number",
        sortable: false,
        valueGetter(_, row) {
          return row.usage.outputTokens;
        },
        valueFormatter: (value) => formatTokensCompact(Number(value ?? 0)),
      },
      {
        field: "thinkingTokens",
        headerName: "Thinking tokens",
        flex: 0.4,
        type: "number",
        sortable: false,
        valueGetter(_, row) {
          return row.usage.thinkingTokens;
        },
        valueFormatter: (value) => formatTokensCompact(Number(value ?? 0)),
      },
      {
        field: "cacheReadTokens",
        headerName: "Cache read tokens",
        flex: 0.42,
        type: "number",
        sortable: false,
        valueGetter(_, row) {
          return row.usage.cacheReadTokens;
        },
        valueFormatter: (value) => formatTokensCompact(Number(value ?? 0)),
      },
      {
        field: "cacheWriteTokens",
        headerName: "Cache write tokens",
        flex: 0.42,
        type: "number",
        sortable: false,
        valueGetter(_, row) {
          return row.usage.cacheWriteTokens;
        },
        valueFormatter: (value) => formatTokensCompact(Number(value ?? 0)),
      },
      {
        field: "estimatedCostUsd",
        headerName: "Estimated cost",
        flex: 0.35,
        type: "number",
        sortable: false,
        valueGetter(_, row) {
          return row.usage.estimatedCostUsd;
        },
        valueFormatter: (value) => formatUsdCost(Number(value ?? 0)),
      },
    ],
    []
  );

  const buildVariables = useCallback(
    (
      omnisearch: string,
      pagination: { page: number; pageSize: number },
      sort: GridSortModel
    ): GetScoutConversationsForStaffQueryVariables => ({
      search: omnisearch || undefined,
      page: pagination.page,
      pageSize: pagination.pageSize,
      sort: sort.map((s) => ({ field: s.field, sort: s.sort })),
    }),
    []
  );

  // Runs in the export-data lambda; the button polls the job and downloads the
  // CSV when it lands. Cache is off because the grid's rows change under it.
  const commandButtons = useCallback(
    ({ search, sortModel }: OmnisearchCommandButtonsContext) => [
      <ExportDataButton
        key="export"
        exportType="scoutConversationsForStaff"
        fileName="scout-conversations"
        filter={{ search, sort: sortModel }}
        disableCache
        iconOnly
        title="Download all conversations matching the current filter as CSV"
      />,
    ],
    []
  );

  const handleRowClick = useCallback(
    (params: GridRowParams<ScoutConversationStaffRow>) => {
      navigate(`/staff/observe/scout-conversation/${params.row.id}`);
    },
    [navigate]
  );

  return (
    <Box sx={{ pt: 3 }}>
      <OmnisearchDataGridV2
        query={GET_SCOUT_CONVERSATIONS_FOR_STAFF}
        getItems={getItems}
        getCount={getCount}
        persistenceKey="staffScoutConversations"
        pageSizeOptions={PAGE_SIZE_OPTIONS}
        initialSortModel={INITIAL_SORT_MODEL}
        filterGroups={FILTER_GROUPS}
        additionalFilterKeys={ADDITIONAL_FILTER_KEYS}
        excludeFilterColumns={EXCLUDE_FILTER_COLUMNS}
        columns={columns}
        commandButtons={commandButtons}
        buildVariables={buildVariables}
        onRowClick={handleRowClick}
      />
    </Box>
  );
}
